<template>
  <div id="app">
    <div class="appbox">
      <template v-if="isStandaloneLayout">
        <router-view />
      </template>
      <template v-else-if="isAdminLayout">
      <div class="admin-shell">
        <AdminSidebar />
        <div class="admin-main">
          <AdminHeader />
          <div class="admin-container">
            <router-view />
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div v-if="globalTopAnnouncements.length" class="global-top-bar">
        <div class="marquee-track">
          <span v-for="a in globalTopAnnouncements" :key="a.id" class="marquee-item">{{ a.title }}：{{ stripHtml(a.content) }}</span>
        </div>
        <span class="marquee-close" @click="dismissGlobalTop">&times;</span>
      </div>
      <Sidebar
        :mobile-open="mobileNavigationOpen"
        @request-member="handleMemberEntry"
        @collapse="handleSidebarCollapse"
        @close-mobile="closeMobileNavigation"
        @navigate="closeMobileNavigation"
      />
      <button
        v-if="mobileNavigationOpen"
        type="button"
        class="mobile-navigation-backdrop"
        aria-label="关闭主导航"
        @click="closeMobileNavigation"
      ></button>
      <div class="main" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
        <Header
          :mobile-navigation-open="mobileNavigationOpen"
          @request-member="handleMemberEntry"
          @open-navigation="openMobileNavigation"
        />
        <div v-if="pageTopAnnouncements.length" class="page-top-bar">
          <div class="marquee-track">
            <span v-for="a in pageTopAnnouncements" :key="a.id" class="marquee-item">{{ a.title }}：{{ stripHtml(a.content) }}</span>
          </div>
          <span class="marquee-close" @click="dismissPageTop">&times;</span>
        </div>
        <div class="container">
          <router-view />
        </div>
      </div>
      <LoginDialog :visible.sync="showMemberLogin" @success="handleMemberLoginSuccess" />
      <MemberDialog
        :visible.sync="memberDialogVisible"
        :loading="memberCatalogLoading || memberBillingLoading || memberPromotionLoading"
        :keep-open-on-close="keepMemberDialogOpenOnClose"
        :user-name="memberUserName"
        :member-i-d="memberUserID"
        :member-identity="memberIdentity"
        :member-level-code="memberLevelCode"
        :expire-label="memberExpireLabel"
        :plans="memberPlans"
        :credit-packages="memberCreditPackages"
        :discounts="activeDiscounts"
        :promotion-cards="memberPromotionCards"
        :stage3-active="isStage3Active"
        :stage3-expired="isStage3Expired"
        :stage3-countdown-label="stage3CountdownLabel"
        :sale-deadline-label="memberPromotionSaleDeadlineLabel"
        :sale-countdown-label="memberPromotionSaleCountdownLabel"
        :sale-deadline-active="memberPromotionSaleActive"
        @closed="handleMemberDialogClosed"
        @payment-success="handleMemberPaymentSuccess"
      />
      <UploadMaterialDialog :visible.sync="uploadMaterialDialogVisible" />
      <AppDrawer :visible.sync="appDrawerVisible" />
      <AnnmtDialog :visible.sync="annmtDialogVisible" />
      <NewDialog
        :visible.sync="newDialogVisible"
        :type="newDialogType"
        :stage1-amount="formatYuanFromCents(stage1Definition && stage1Definition.amount_cents || 0)"
        :stage2-amount="formatYuanFromCents(stage2Definition && stage2Definition.amount_cents || 0)"
        :stage3-amount="formatYuanFromCents(stage3Definition && stage3Definition.amount_cents || 0)"
        :stage1-coupons="stage1CouponItems"
        :stage2-tip="promotionDescription(stage2Definition, '领取后可用于会员套餐自动抵扣')"
        :stage3-title="promotionTitle(stage3Definition, '红包膨胀券')"
        :countdown-label="stage3CountdownLabel"
        @claim="handleNewDialogClaim"
      />
    </template>
    </div>
  </div>
</template>

<script>
import memberFlow from "@/mixins/memberFlow";
import Sidebar from "./components/Sidebar.vue";
import Header from "@/components/Header.vue";
import AdminSidebar from "@/components/AdminSidebar.vue";
import AdminHeader from "@/components/AdminHeader.vue";
import LoginDialog from "@/components/LoginDialog.vue";
import MemberDialog from "@/components/MemberDialog.vue";
import NewDialog from "@/components/NewDialog.vue";
import AppDrawer from "@/components/AppDrawer.vue";
import UploadMaterialDialog from "@/components/UploadMaterialDialog.vue";
import AnnmtDialog from "@/components/AnnmtDialog.vue";
import { restoreSession } from "@/services/auth";
import { loginDialogEventName } from "@/services/api";
import {
  clearMemberBillingOverview,
} from "@/services/memberStatus";
import {
  clearMyCreditsOverview,
  refreshMyCreditsOverview,
} from "@/services/myCredits";
import { clearMyPaymentOrders } from "@/services/myPaymentOrders";
import { fetchActiveAnnouncements, markAnnouncementRead } from "@/services/announcement";

export default {
  name: "App",
  mixins: [memberFlow],
  components: {
    Sidebar,
    Header,
    AdminSidebar,
    AdminHeader,
    LoginDialog,
    MemberDialog,
    NewDialog,
    AppDrawer,
    UploadMaterialDialog,
    AnnmtDialog,
  },
  data() {
    return {
      sidebarCollapsed: false,
      mobileNavigationOpen: false,
      uploadMaterialDialogVisible: false,
      appDrawerVisible: false,
      annmtDialogVisible: false,
      globalTopAnnouncementsRaw: [],
      pageTopAnnouncementsRaw: [],
      globalTopDismissed: false,
      pageTopDismissed: false,
    };
  },
  computed: {
    isStandaloneLayout() {
      return !!(this.$route.meta && this.$route.meta.standaloneLayout);
    },
    isAdminLayout() {
      return !!(this.$route.meta && this.$route.meta.adminLayout);
    },
    globalTopAnnouncements() {
      if (this.globalTopDismissed) return [];
      return this.globalTopAnnouncementsRaw.filter((a) => !a.is_read);
    },
    pageTopAnnouncements() {
      if (this.pageTopDismissed) return [];
      return this.pageTopAnnouncementsRaw.filter((a) => !a.is_read);
    },
  },
  watch: {
    "$route.path"() {
      this.closeMobileNavigation();
    },
    isAuthenticated: {
      immediate: true,
      handler(value) {
        if (value) {
          this.ensureStage3Timer();
          this.loadMembershipCatalog();
          this.loadMemberPromotions();
          this.loadCurrentSpaceBilling();
          this.loadAnnouncements();
          refreshMyCreditsOverview().catch(() => {});
          return;
        }
        this.resetMemberPromotionState();
        this.stopStage3Timer();
        this.membershipPlansCatalog = [];
        this.creditPackagesCatalog = [];
        clearMyCreditsOverview();
        clearMyPaymentOrders();
        clearMemberBillingOverview();
      },
    },
  },
  created() {
    restoreSession();
    if (this.$bus) {
      this.$bus.$on("openPublishDialog", this.handleOpenPublishDialog);
      this.$bus.$on("openAllDrawer", this.handleOpenAllDrawer);
    }
    if (typeof window !== "undefined") {
      window.addEventListener(loginDialogEventName, this.handleOpenLoginDialog);
      window.addEventListener("resize", this.handleViewportResize);
    }
  },
  beforeDestroy() {
    if (this.$bus) {
      this.$bus.$off("openPublishDialog", this.handleOpenPublishDialog);
      this.$bus.$off("openAllDrawer", this.handleOpenAllDrawer);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener(loginDialogEventName, this.handleOpenLoginDialog);
      window.removeEventListener("resize", this.handleViewportResize);
    }
    this.clearMemberFlowTimer();
    this.stopStage3Timer();
  },
  methods: {
    handleSidebarCollapse(collapsed) {
      this.sidebarCollapsed = collapsed;
    },
    openMobileNavigation() {
      this.mobileNavigationOpen = true;
    },
    closeMobileNavigation() {
      this.mobileNavigationOpen = false;
    },
    handleViewportResize() {
      if (window.innerWidth >= 768) {
        this.closeMobileNavigation();
      }
    },
    handleOpenPublishDialog(visible) {
      this.uploadMaterialDialogVisible = !!visible;
    },
    handleOpenLoginDialog() {
      this.showMemberLogin = true;
    },
    handleOpenAllDrawer(visible) {
      this.appDrawerVisible = !!visible;
    },
    dismissGlobalTop() {
      this.globalTopDismissed = true;
      for (const a of this.globalTopAnnouncementsRaw) {
        if (!a.is_read) markAnnouncementRead(a.id).catch(() => {});
      }
    },
    dismissPageTop() {
      this.pageTopDismissed = true;
      for (const a of this.pageTopAnnouncementsRaw) {
        if (!a.is_read) markAnnouncementRead(a.id).catch(() => {});
      }
    },
    stripHtml(html) {
      if (!html) return "";
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || "";
    },
    async loadAnnouncements() {
      try {
        const [globalTop, pageTop, popup] = await Promise.all([
          fetchActiveAnnouncements("global_top"),
          fetchActiveAnnouncements("page_top"),
          fetchActiveAnnouncements("popup"),
        ]);
        this.globalTopAnnouncementsRaw = globalTop || [];
        this.pageTopAnnouncementsRaw = pageTop || [];
        this.globalTopDismissed = false;
        this.pageTopDismissed = false;
        const unreadPopup = (popup || []).filter((a) => !a.is_read);
        if (unreadPopup.length > 0) {
          this.annmtDialogVisible = true;
        }
      } catch (_) { /* ignore */ }
    },
  },
};
</script>

<style lang="scss">
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #f5f7fa;
  color: #333;
}

html,
body,
#app {
  height: 100%;
  background: linear-gradient(
    to right,
    #ffffff 0%,
    #F2F5F8 calc(50% - 1000px),
    #f0f4f9 calc(50% + 1000px),
    #ffffff 100%
  );
}
.appbox {
  max-width: 2000px;
  margin: 0 auto;
  position: relative;
  min-height: 100vh;
}
div {
  line-height: 1;
}

.main {
  margin-left: 210px;
  min-width: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.25s ease;
}

.main.sidebar-collapsed {
  margin-left: 60px;
}

.mobile-navigation-backdrop {
  display: none;
}

.global-top-bar,
.page-top-bar {
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, #fff7ed, #fff0e0);
  color: #d35400;
  font-size: 13px;
  padding: 6px 16px;
  overflow: hidden;
  position: relative;
  z-index: 100;
}
.global-top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
}
.global-top-bar ~ .sidebar {
  top: 30px;
}
.global-top-bar ~ .main {
  margin-top: 30px;
}
.marquee-track {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  animation: marquee-scroll 60s linear infinite;
}
.marquee-item {
  display: inline-block;
  padding-right: 60px;
}
.marquee-close {
  cursor: pointer;
  font-size: 16px;
  padding: 4px 8px;
  color: #c0392b;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
}
@keyframes marquee-scroll {
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
}

.container {
  flex: 1;
  overflow-y: auto;
  background-color: #f0f4f9;
  padding: 0;
  position: relative;
}

.admin-shell {
  min-height: 100vh;
  display: flex;
  background-color: #f0f4f9;
  max-width: none;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  width: 100vw;
}

.admin-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.admin-container {
  flex: 1;
  overflow-y: auto;
  background-color: #f0f4f9;
}

@media (max-width: 767px) {
  .main,
  .main.sidebar-collapsed {
    width: 100%;
    margin-left: 0;
  }

  .mobile-navigation-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 3000;
    width: 100%;
    height: 100%;
    border: 0;
    background: rgba(0, 0, 0, 0.38);
    cursor: pointer;
  }
}
/* ... 你的其他样式 ... */
/* 1. 所有输入框、下拉框、文本域 默认边框 */
.el-input__inner,
.el-textarea__inner,
.el-select__inner {
  border-color: #e4e7ed !important; /* 正常灰色边框 */
}

/* 2. 输入框 / 下拉框 / 文本域 聚焦高亮边框（主题色） */
.el-input.is-active .el-input__inner,
.el-input__inner:focus,
.el-textarea.is-active .el-textarea__inner,
.el-textarea__inner:focus,
.el-select.is-active .el-select__inner,
.el-select__inner:focus {
  border-color: #ff7d00 !important; /* 主题橙色 */
}

/* 3. 下拉菜单选中项背景色（你截图要的） */
.el-select-dropdown__item.selected {
  color: #ff7d00 !important;
}

/* 4. 下拉菜单 hover 颜色 */
.el-select-dropdown__item:hover {
  background-color: #fff2e6 !important;
  color: #ff7d00 !important;
}

/* 5. 全局按钮主题色（统一风格） */
.el-button--primary {
  background-color: #ff7d00 !important;
  border-color: #ff7d00 !important;
}

/* 6. 复选框 / 单选框 选中颜色 */
.el-checkbox__input.is-checked .el-checkbox__inner,
.el-radio__input.is-checked .el-radio__inner {
  background-color: #ff7d00 !important;
  border-color: #ff7d00 !important;
}
// 复选框 / 单选框 鼠标放上颜色
.el-checkbox__input:hover .el-checkbox__inner,
.el-radio__input:hover .el-radio__inner {
  border-color: #ff7d00 !important;
}
// 按钮颜色
.el-button--primary:hover {
  background-color: #e67400 !important;
  border-color: #e67400 !important;
}
.el-button--primary:active {
  background-color: #d96c00 !important;
  border-color: #d96c00 !important;
}
.el-button--primary:focus {
  background-color: #ff7d00 !important;
  border-color: #ff7d00 !important;
}
.el-button--primary:disabled {
  background-color: #ff9933 !important;
  border-color: #ff9933 !important;
}
.el-button--text {
  color: #ff7d00 !important;
}
/* 全局滚动条美化 - 整个项目生效 */
::-webkit-scrollbar {
  width: 4px; /* 竖轴滚动条宽度 */
  height: 4px; /* 横轴滚动条高度 */
}

/* 滚动条滑块 */
::-webkit-scrollbar-thumb {
  background: rgba($color: #000000, $alpha: 0.5);
  border-radius: 2px;
}

/* 滚动条轨道 */
::-webkit-scrollbar-track {
  background: #f5f5f5;
}
</style>
